import _ from "lodash";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import ReactTooltip from "react-tooltip";

import {
  getSponsorWindow,
  postCreateSponsorWindow,
  postDeleteSponsorWindow,
  postUpdateEventSponsorWindow,
  postUpdateSponsorWindow,
  postUploadSponsorWindow,
} from "../../../../apis";
import CloseIcon from "../../../../assets/icons/CloseIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import Button from "../../../../components/common/Button";
import {
  InputFile,
  InputText,
  TextareaEditable,
} from "../../../../components/common/Input";
import RadioButton from "../../../../components/common/RadioButton";
import Title from "../../../../components/common/Title";
import {
  MAX_UPLOAD_IMAGE_SIZE,
  RESIZE_IMAGE_SUBDOMAIN,
} from "../../../../constants";
import { isValidHttpsWebsite } from "../../../../functions";
import { useEvent } from "../../../../Store/Event/Show";
import SwitchWithTitle from "../../../GeneralSettings/components/SwitchWithTitle";
import Information from "../../../../assets/images/information.png";

const DEFAULT_STATE = {
  url: "",
  title: "",
  cta: "",
  description: "",
  header: "",
  link: "",
  id: null,
};

const SponsorWindow = () => {
  const { eventId } = useParams();

  const { event, setEvent } = useEvent();

  const [showCreateSponsorWindow, setShowCreateSponsorWindow] = useState(false);
  const [sponsorWindow, setSponsorWindow] = useState({
    url: "",
    title: "",
    cta: "",
    description: "",
    header: "",
    link: "",
    id: null,
  });
  const [error, setErrors] = useState({
    url: "",
    title: "",
    cta: "",
    description: "",
    header: "",
    link: "",
  });
  const [isImageSelected, setIsImageSelected] = useState(false);
  const [uploadLoader, setUploadLoader] = useState(false);

  const onSave = async () => {
    try {
      if (sponsorWindow.cta && !sponsorWindow.link) {
        window.Toast.fire({
          icon: "error",
          title: "Link must be provided with CTA text",
        });
        return;
      }
      if (sponsorWindow.link && !isValidHttpsWebsite(sponsorWindow.link)) {
        window.Toast.fire({
          icon: "error",
          title: "Provided link is not a valid link",
        });
        return;
      }
      if (!sponsorWindow.header && !sponsorWindow.url && !sponsorWindow.title) {
        window.Toast.fire({
          icon: "error",
          title: "Header , sponsor image or title is needed",
        });
        return;
      }
      if (sponsorWindow.id) {
        const payload = structuredClone(sponsorWindow);

        if (sponsorWindow.url && isImageSelected) {
          payload.title = "";
          payload.description = "";
        } else {
          payload.url = "";
        }
        payload.eventId = +eventId;

        await postUpdateSponsorWindow(payload);
        window.Toast.fire({
          icon: "success",
          title: "Data updated successfully.",
        });
      } else {
        const payload = structuredClone(sponsorWindow);

        if (sponsorWindow.url) {
          payload.title = "";
          payload.description = "";
        } else {
          payload.url = "";
        }
        payload.eventId = +eventId;
        delete payload.id;

        const { data } = await postCreateSponsorWindow(payload);
        window.Toast.fire({
          icon: "success",
          title: "Sponsor window created successfully.",
        });
        setSponsorWindow((prev) => ({ ...prev, id: data.id }));
      }
    } catch (error) {
      console.error(error);
      window.Toast.fire({
        icon: "error",
        title: "Something went wrong.",
      });
      callSponsorWindow();
      return false;
    }
  };

  const uploadImage = async (file) => {
    if (!uploadLoader) {
      if (file.size && file.size > MAX_UPLOAD_IMAGE_SIZE) {
        window.Toast.fire({
          icon: "error",
          title: "Image size is more than 6 MB",
        });
        return;
      }
      setUploadLoader(true);
      try {
        const data = await postUploadSponsorWindow(file, eventId);
        setSponsorWindow((prev) => ({ ...prev, url: data }));
      } catch (error) {
        console.error(error);
        window.Toast.fire({
          icon: "error",
          title: "Something went wrong",
        });
      } finally {
        setUploadLoader(false);
      }
    }
  };

  const callSponsorWindow = async () => {
    try {
      const { data } = await getSponsorWindow(eventId);
      if (Object.keys(data).length) {
        if (data.url) setIsImageSelected(true);
        setSponsorWindow(data);
      }
    } catch (error) {
      console.error(error);
      window.Toast.fire({
        icon: "error",
        title: "Unable to fetch sponsor list",
      });
    }
  };

  const onRemove = async () => {
    Swal.fire({
      title: "Delete Sponsor Window",
      text: "Are you sure you want to delete this sponsor window ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      showLoaderOnConfirm: true,
      preConfirm: () => {
        return postDeleteSponsorWindow({
          sponsorId: sponsorWindow.id,
          eventId: eventId,
        })
          .then((response) => {
            window.Toast.fire({
              icon: "success",
              title: "Your sponsor has been deleted successfully",
            });
            setSponsorWindow(DEFAULT_STATE);
            setIsImageSelected(false);
          })
          .catch((err) => {
            console.error(err);
            Swal.fire(
              "Error!",
              err.response?.data?.message ?? "Something went wrong",
              "error"
            );
          });
      },
    });
  };

  const updateEventSponsorField = async (value) => {
    try {
      setEvent((prev) => ({
        ...prev,
        showSponsorWindow: value,
      }));
      await postUpdateEventSponsorWindow({ eventId, value });
    } catch (error) {
      console.error(error);
      setEvent((prev) => ({
        ...prev,
        showSponsorWindow: !value,
      }));
    }
  };

  useEffect(() => {
    callSponsorWindow();
  }, []);

  return (
    <div>
      <Title
        className="pb-4 mb-6 border-b border-secondary-600 mt-4"
        text={
          <>
            <div>Sponsor Window</div>{" "}
            <span
              className="pl-3 cursor-pointer relative"
              data-tip
              data-for="sponsorWindowTip"
            >
              <img className="cursor-pointer" src={Information} />
              <ReactTooltip
                id="sponsorWindowTip"
                multiline={true}
                type="dark"
                className="z-[100] !opacity-100"
              >
                <div>
                  <h3 className="text-xl font-medium mb-1">Feature Overview</h3>
                  <ul className="list-disc px-4 mb-2 text-sm">
                    <li>
                      Create branded pop-ups that showcase your company details
                      and encourage users to connect with you directly.
                    </li>
                  </ul>
                  <div className="w-full mt-2 mb-2 h-[1px] bg-[#555555]"></div>
                  <h3 className="text-xl font-medium mb-1">How to use</h3>
                  <ol className="list-decimal px-4 mb-2 text-sm">
                    <li>
                      Add header text
                      <li>Select text or image depending</li>
                      <li>
                        For image upload the image or add the title and
                        description for text selection
                      </li>
                      <li>Add CTA text if needed and add hyperlink with it</li>
                      <li>Click on save button.</li>
                    </li>
                  </ol>
                  <video
                    muted
                    autoPlay
                    loop
                    playsinline
                    src={
                      "https://img2.pixnif.com/assets/videos/sponsorWindow.mov"
                    }
                    height={400}
                    width={400}
                  />
                </div>
              </ReactTooltip>
            </span>
          </>
        }
      />
      <div className="w-[calc(50%-8rem)]">
        <SwitchWithTitle
          className="mb-8 font-medium"
          title="Enable Sponsor Window"
          value={event.showSponsorWindow}
          onChange={() => {
            event.showSponsorWindow
              ? updateEventSponsorField(0)
              : updateEventSponsorField(1);
          }}
        />
      </div>
      {event.showSponsorWindow ? (
        <div className="flex gap-8 text-black">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="text-base text-secondary-300 font-medium">
                Header
              </div>
              <InputText
                id="header"
                value={sponsorWindow.header}
                onChange={(e) =>
                  setSponsorWindow((prev) => ({ ...prev, header: e }))
                }
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="text-base font-medium text-secondary-300">
                Select window content
              </div>
              <div className="flex flex-row justify-between gap-5">
                <div className="flex flex-col w-full">
                  <RadioButton
                    width={"w-full"}
                    checked={!isImageSelected}
                    handleOnClick={() => {
                      if (!sponsorWindow.id) {
                        setSponsorWindow((prev) => ({ ...prev, url: null }));
                      }
                      setIsImageSelected(false);
                    }}
                    label="Text"
                  />
                </div>

                <div className="flex flex-col w-full">
                  <RadioButton
                    width={"w-full"}
                    checked={isImageSelected}
                    handleOnClick={() => {
                      if (!sponsorWindow.id) {
                        setSponsorWindow((prev) => ({
                          ...prev,
                          title: null,
                          description: null,
                        }));
                      }
                      setIsImageSelected(true);
                    }}
                    label="Image"
                  />
                </div>
              </div>
            </div>
            <div>
              {isImageSelected ? (
                <InputFile
                  className=""
                  showLoader={uploadLoader}
                  label="Upload Sponsor Image"
                  overlapLabel={
                    uploadLoader ? (
                      <div className="clipPathLoader"></div>
                    ) : (
                      "Upload"
                    )
                  }
                  disabled={uploadLoader}
                  name="logo"
                  allowedFileTypes={[".png", ".jpg", ".jpeg"]}
                  fileName={
                    _.truncate(
                      sponsorWindow.url
                        ? sponsorWindow.url.split("/").reverse()[0]
                        : ""
                    ) ?? null
                  }
                  onFileChange={uploadImage}
                  showExplorer
                />
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <div className="text-base text-secondary-300 font-medium">
                      Title
                    </div>
                    <InputText
                      id="title"
                      value={sponsorWindow.title}
                      onChange={(e) =>
                        setSponsorWindow((prev) => ({ ...prev, title: e }))
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="text-base text-secondary-300 font-medium">
                      Description
                    </div>
                    <TextareaEditable
                      id="description"
                      required={false}
                      actionText=""
                      value={sponsorWindow.description}
                      onChange={(e) =>
                        setSponsorWindow((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      textAreaHeight="min-h-[2rem]"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-4">
              <div className="flex flex-col gap-2">
                <div className="text-base text-secondary-300 font-medium">
                  CTA Text
                </div>
                <InputText
                  id="CTA text"
                  value={sponsorWindow.cta}
                  onChange={(e) =>
                    setSponsorWindow((prev) => ({ ...prev, cta: e }))
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className="text-base text-secondary-300 font-medium">
                  Hyperlink
                </div>
                <InputText
                  id="link"
                  value={sponsorWindow.link}
                  onChange={(e) =>
                    setSponsorWindow((prev) => ({ ...prev, link: e }))
                  }
                />
              </div>
            </div>
            <div className="flex gap-12  items-center">
              <button
                role="button"
                title="Save"
                onClick={onSave}
                className={`font-medium cursor-pointer !bg-dark-gray text-white flex gap-4 items-center rounded-xl shadow-card  px-3 py-2.5 w-fit`}
              >
                Save
              </button>
            </div>
          </div>
          <div className="mx-auto flex flex-col gap-4 mt-8 items-center text-white">
            <h1 className="text-xl text-left mr-auto">Preview</h1>
            <div className="bg-white w-[250px] max-h-[250px] md:w-[300px] md:max-h-[300px] rounded-xl text-black ">
              <h2 className="text-xl md:text-2xl truncate  pl-4 pt-4 pb-2 mb-4 shadow-xl border-b border-b-[1px]">
                {sponsorWindow.id
                  ? sponsorWindow.header
                    ? sponsorWindow.header
                    : ""
                  : sponsorWindow.header || "HEADER"}
              </h2>
              {sponsorWindow.url && isImageSelected ? (
                <img
                  className="max-h-[150px] md:max-h-[200px] mb-4 mx-auto object-contain"
                  src={"https://i2.pixnif.com/" + sponsorWindow.url}
                  alt="sponsorWindowUrl"
                />
              ) : (
                <>
                  <h3 className="text-lg md:text-xl truncate  pl-4 pb-4  ">
                    {sponsorWindow.id
                      ? sponsorWindow.title
                        ? sponsorWindow.title
                        : ""
                      : sponsorWindow.title || "TITLE"}
                  </h3>
                  <p className="pl-4 pr-4 overflow-y-auto scrollbar-sponsor h-[100px] md:h-[150px] break-words mb-4">
                    {sponsorWindow.id
                      ? sponsorWindow.description
                        ? sponsorWindow.description
                        : ""
                      : sponsorWindow.description || "DESCRIPTION"}
                  </p>
                </>
              )}
              {(sponsorWindow.id && sponsorWindow.cta) || !sponsorWindow.id ? (
                <Button
                  className="!w-full !h-[3rem] truncate !rounded-none !rounded-b-xl"
                  text={
                    sponsorWindow.id
                      ? sponsorWindow.cta
                        ? sponsorWindow.cta
                        : ""
                      : sponsorWindow.cta || "CTA Button"
                  }
                />
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="h-[512px]"></div>
      )}
    </div>
  );
};

export default SponsorWindow;
